import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { requestPasswordReset, confirmPasswordReset } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plane, Mail, Lock, KeyRound, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await requestPasswordReset(email);
      toast.success('Código enviado para seu e-mail');
      setStep(2);
    } catch (err) {
      toast.error('Erro ao enviar código');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(email, code, newPassword);
      toast.success('Senha redefinida com sucesso!');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-zinc-100">
      {/* Image Side - Oculto no telemóvel (hidden) e visível em computadores (md:flex) */}
      <div 
        className="hidden md:flex relative w-1/2 min-h-screen bg-no-repeat bg-cover bg-center"
        style={{
          backgroundImage: 'url("/img/Simulados-VOAR.png")',
          filter: 'none',
          opacity: 1,
          backgroundColor: 'transparent',
          backgroundBlendMode: 'normal'
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-end p-12 bg-gradient-to-t from-black/60 via-black/10 to-transparent">
          <h1 className="text-5xl font-black text-white mb-4">
            Simulados V.O.A.R.
          </h1>
          <p className="text-xl text-zinc-300 max-w-md">
            Recupere sua senha para continuar seus estudos.
          </p>
        </div>
      </div>

      {/* Form Side - Centralizado e totalmente responsivo no ecrã do celular */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4 sm:p-12 bg-zinc-100">
        <div className="w-full max-w-md bg-white p-6 sm:p-8 rounded-xl shadow-sm md:bg-transparent md:p-0 md:shadow-none md:rounded-none">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo-voar.png" alt="Simulados V.O.A.R." className="h-12" />
            <div>
              <h2 className="text-lg font-bold text-zinc-900">Simulados V.O.A.R.</h2>
              <p className="text-xs text-zinc-500">Simulados exclusivos para alunos do Método V.O.A.R.</p>
            </div>
          </div>

          {/* Back Link */}
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-900 mb-8 transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para login
          </Link>

          {step === 1 && (
            <>
              <h3 className="text-3xl font-bold text-zinc-900 mb-2">
                Recuperar Senha
              </h3>
              <p className="text-zinc-500 mb-8">
                Digite seu e-mail para receber um código de recuperação.
              </p>

              <form onSubmit={handleRequestCode} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="form-label">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 z-10" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@email.com"
                      className="h-12 pl-12 bg-white border border-zinc-200 rounded-lg focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                      required
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-medium rounded-lg btn-press"
                  disabled={loading}
                  data-testid="send-code-btn"
                >
                  {loading ? 'Enviando...' : 'Enviar Código'}
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="text-3xl font-bold text-zinc-900 mb-2">
                Redefinir Senha
              </h3>
              <p className="text-zinc-500 mb-8">
                Digite o código recebido por e-mail e sua nova senha.
              </p>

              <form onSubmit={handleResetPassword} className="space-y-5">
                <div>
                  <Label htmlFor="code" className="form-label">Código</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 z-10" />
                    <Input
                      id="code"
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="123456"
                      className="h-12 pl-12 bg-white border border-zinc-200 rounded-lg focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                      required
                      data-testid="reset-code-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="newPassword" className="form-label">Nova Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 z-10" />
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-12 pl-12 bg-white border border-zinc-200 rounded-lg focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                      required
                      data-testid="new-password-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="form-label">Confirmar Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 z-10" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-12 pl-12 bg-white border border-zinc-200 rounded-lg focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-medium rounded-lg btn-press"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? 'Redefinindo...' : 'Redefinir Senha'}
                </Button>
              </form>
            </>
          )}

          {step === 3 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-3xl font-bold text-zinc-900 mb-2">
                Senha Redefinida!
              </h3>
              <p className="text-zinc-500 mb-8">
                Sua senha foi alterada com sucesso. Faça login com sua nova senha.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-medium rounded-lg btn-press"
                data-testid="go-to-login-btn"
              >
                Ir para Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
